import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Subject } from 'rxjs';

import { MediaService } from '../../../shared/services/media.service';

import { EMediaLoaderType } from '../../../shared/enums/media-loader-type.enum';

@Component({
  selector: 'stxt-thumbnail-loader',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './thumbnail-loader.component.html',
  styleUrl: './thumbnail-loader.component.scss'
})
export class ThumbnailLoaderComponent implements OnInit, OnDestroy {
  @Input() generationId: string;
  @Input() isNsfw: boolean = false;

  @Input() set usageCase(value: EMediaLoaderType) {
    if (!value || this._usageCase === value) return;
    this._usageCase = value;
    this.resetProgress();
  }

  private _usageCase: EMediaLoaderType;
  protected readonly EMediaLoaderType = EMediaLoaderType;

  get usageCase(): EMediaLoaderType {
    return this._usageCase;
  }

  messages: string[] = [
    'Preparing your image',
    'Working on the details',
    'Adding finishing touches',
    'Almost there'
  ];

  messageRanges: { message: string; start: number; end: number }[] = [
    { message: 'Preparing your image', start: 0, end: 30 },
    { message: 'Working on the details', start: 30, end: 60 },
    { message: 'Adding finishing touches', start: 60, end: 90 },
    { message: 'Almost there', start: 90, end: 100 }
  ];

  currentMessage: string = this.messages[0];
  currentIndex: number = 0;
  fadeOut: boolean = false;
  percentage: number = 0;
  displayPercentage: string = '0';
  private microProgressStep: number = 0;
  private microProgressInterval: ReturnType<typeof setInterval> | null = null;
  private startTime: number = Date.now();

  private destroy$: Subject<void> = new Subject<void>();
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  private readonly fadeDuration: number = 300;
  private readonly MICRO_PROGRESS_DELAY: number = 500;
  private readonly MICRO_STEPS: number[] = [99.1, 99.2, 99.3, 99.4, 99.5];
  private readonly SFW_DURATION: number = 30000;
  private readonly NSFW_DURATION: number = 60000;

  constructor(private readonly mediaService: MediaService) {}

  ngOnInit(): void {
    this.resetProgress();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAllIntervals();
  }

  private resetProgress(): void {
    this.clearAllIntervals();
    this.startTime = Date.now();
    this.percentage = 0;
    this.displayPercentage = '0';
    this.microProgressStep = 0;
    this.startProgressTracking();
  }

  private clearAllIntervals(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    if (this.microProgressInterval) {
      clearInterval(this.microProgressInterval);
      this.microProgressInterval = null;
    }
  }

  private setProgress(value: number): void {
    this.percentage = value;
    this.displayPercentage = this.formatPercentage(value);
  }

  private formatPercentage(value: number): string {
    if (value >= 100) return '100';
    if (value >= 99) return value.toFixed(1);
    return Math.floor(value).toString();
  }

  private updateMessageBasedOnPercentage(percentage: number): void {
    const messageRange = this.messageRanges.find(
      range => percentage >= range.start && percentage <= range.end
    );

    if (messageRange && this.currentMessage !== messageRange.message) {
      this.fadeOut = true;
      setTimeout(() => {
        this.currentMessage = messageRange.message;
        this.currentIndex = this.messages.indexOf(messageRange.message);
        this.fadeOut = false;
      }, this.fadeDuration);
    }
  }

  private calculateProgressBasedOnTime(): number {
    const elapsed = Date.now() - this.startTime;
    const totalDuration = this.isNsfw ? this.NSFW_DURATION : this.SFW_DURATION;
    const baseProgress = Math.min(98, (elapsed / totalDuration) * 100);

    // For chat mode, use faster linear progress
    if (this.usageCase === EMediaLoaderType.Chat) {
      return Math.min(99, baseProgress * 1.5); // Accelerate progress by 50%
    }

    return Math.min(98, baseProgress * (1 - Math.pow(0.98, elapsed / 1000)));
  }

  private startProgressTracking(): void {
    // Skip if we need generationId but don't have it
    if (this.usageCase !== EMediaLoaderType.Chat && !this.generationId) return;

    this.progressInterval = setInterval(() => {
      const timeBasedProgress = this.calculateProgressBasedOnTime();
      const serviceProgress = this.generationId
        ? this.mediaService.getGenerationProgress(this.generationId)
        : timeBasedProgress;
      const newPercentage = Math.max(serviceProgress, timeBasedProgress);
      if (newPercentage >= 99 && this.percentage < 99 && newPercentage !== 100) {
        this.setProgress(99);
        this.updateMessageBasedOnPercentage(99);
        this.startMicroProgress();
      } else if (newPercentage < 99 || newPercentage === 100) {
        this.setProgress(newPercentage);
        this.updateMessageBasedOnPercentage(newPercentage);
      }
    }, 100);
  }

  private startMicroProgress(): void {
    if (this.microProgressInterval) {
      clearInterval(this.microProgressInterval);
    }

    if (this.percentage === 99) {
      this.microProgressStep = 0;
    }

    this.microProgressInterval = setInterval(() => {
      if (this.microProgressStep < this.MICRO_STEPS.length) {
        this.setProgress(this.MICRO_STEPS[this.microProgressStep]);
        this.microProgressStep++;
      } else {
        clearInterval(this.microProgressInterval);
        this.microProgressInterval = null;
      }
    }, this.MICRO_PROGRESS_DELAY);
  }
}
